import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { safeArticleHTML } from "@/lib/safeHtml";

interface ArticleQuickPreviewProps {
  article: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  review: "In Review",
  revision: "Revision",
  approved: "Approved",
  scheduled: "Scheduled",
  published: "Published",
  archived: "Archived",
};

export default function ArticleQuickPreview({ article, open, onOpenChange }: ArticleQuickPreviewProps) {
  if (!article) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl leading-tight">{article.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            <Badge variant="outline">{STATUS_LABELS[article.status] || article.status}</Badge>
            {article.categories?.name && <Badge variant="secondary">{article.categories.name}</Badge>}
            <span>{format(new Date(article.updated_at), "dd MMM yyyy HH:mm")}</span>
            {article.profiles?.full_name && <span>· {article.profiles.full_name}</span>}
          </div>
          {article.featured_image_url && (
            <img src={article.featured_image_url} alt={article.title} className="w-full h-48 object-cover rounded-lg" />
          )}
          {article.excerpt && (
            <p className="text-sm text-muted-foreground italic">{article.excerpt}</p>
          )}
          {article.content ? (
            <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: safeArticleHTML(article.content) }} />
          ) : (
            <p className="text-sm text-muted-foreground">No content yet.</p>
          )}
          {article.tags?.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {article.tags.map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
